import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Summary } from "./summary";
import { ServiceAccount } from "../../schema/types/serviceAccount/entity";
import { ServiceAccountDetailHeader } from "./header";
import { Keys } from "./keys";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

interface Props {
  serviceAccount: ServiceAccount;
  refetch?: () => void;
}
export const ServiceAccountDetailContent: React.FC<Props> = ({ serviceAccount, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/serviceaccount/:serviceaccount/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(serviceAccount).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(serviceAccount.lastModified);
  }, [serviceAccount.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Keys", "/keys"),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ServiceAccountDetailHeader serviceAccount={serviceAccount} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary serviceAccount={serviceAccount} />} />
              <Route path={`keys/*`} element={<Keys serviceAccount={serviceAccount} />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"serviceaccount"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail
                    kind={"serviceaccount"}
                    itemName={serviceAccount.name}
                    resourceId={serviceAccount.id}
                    version={serviceAccount.version}
                    selfLink={linksOf(serviceAccount).self!}
                    refetch={refetch}
                  />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(serviceAccount).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
